import type { FC, ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

import { useReadQuery } from '@apollo/client/react';
import type {
  AuthenticationState,
  LoginCredentials,
  LoginResult,
  User,
} from '@opendoor/auth-fe/context';
import {
  DEFAULT_LOGIN_DISPLAY_ERROR_MESSAGE,
  parseAuthentication,
} from '@opendoor/auth-fe/context';
import { LOGIN_LITE_ACCESS_TOKEN_QUERY_PARAM } from '@opendoor/auth-fe/globals';
import { UserSessionDocument } from '__generated__/graphql';
import { useLazyQuery } from 'platform/apollo';
import { useTrackLoader } from 'platform/components/loader/LoaderContext';
import { SystemError, toError, UnauthenticatedError } from 'platform/Error';
import { getLoginUrl } from 'platform/loginRedirect';
import navigationHelpers from 'platform/navigationHelpers';
import { globalTrackError } from 'platform/observability';
import { setImpersonatedCustomer, useImpersonatedCustomer } from 'platform/readOnlyMutations';
import { getSupportImpersonationConfig } from 'platform/supportImpersonation';
import { authQueryRef } from 'preloadQueries';
import { useLocation } from 'react-router';
import { clearGraphQLStores } from 'setupClient';
import { ENV } from 'shared/helpers/env';
import { LOGOUT_URL } from 'shared/helpers/externalRoutes';
import { COSMOS_DOMAIN } from 'shared/helpers/globals';

// oxlint-disable-next-line @opendoor/no-passthrough-reexport -- cake forbids importing @opendoor/auth-fe/context directly (see no-restricted-imports); this adapter is the sanctioned boundary for those types.
export type { LoginCredentials, User };

type AuthenticatedCustomer = {
  state: 'authenticated';
  /**
   * The authenticated session holder. During impersonation (?customer=UUID),
   * this is the ops user — NOT the customer being viewed.
   */
  user: User;
  customerUUID: string;
  /**
   * The human token for the customer being viewed. During normal usage this
   * equals user.humanToken. During impersonation it comes from the human_token
   * URL param and may differ from user.humanToken, because user always
   * reflects the ops user's session while this reflects the customer.
   */
  customerHumanToken: string;
};

type Authenticated = {
  state: 'authenticated';
  /** @see AuthenticatedCustomer.user */
  user: User;
  customerUUID: string | null;
  /** @see AuthenticatedCustomer.customerHumanToken */
  customerHumanToken: string;
  /**
   * True when an internal ops user is viewing a customer's account via ?customer=UUID.
   * The authenticated `user` is the ops user; `customerUUID` is the customer being viewed.
   */
  isImpersonating: boolean;
};

type NotAuthenticated = {
  state: 'authenticating' | 'not_authenticated' | 'error';
  user: null;
  customerUUID: null;
  customerHumanToken: null;
  isImpersonating: false;
};

export type AuthInfo = Authenticated | NotAuthenticated;

type AuthFns = {
  /**
   * Trigger an auth refresh
   *
   * @throws if refresh fails
   */
  refresh: () => Promise<void>;

  /**
   * Log out the signed-in user
   */
  logout: () => Promise<void>;

  /**
   * Attempt to verify login
   *
   * @param credentials
   * @returns the result
   */
  verifyLogin: (credentials: LoginCredentials) => Promise<LoginResult>;
};

export type AuthContext = AuthInfo & AuthFns;

export const authContext = createContext<AuthContext | null>(null);

export const useAuthContext = () => {
  const context = useContext(authContext);

  if (!context) {
    throw new SystemError('useAuthContext must be called within an AuthProvider');
  }

  return context;
};

type AuthProviderProps = {
  children: ReactNode;
};

type ImpersonationRedirectUrlOptions = {
  /** Current browser URL whose route, query, and fragment must be preserved. */
  currentUrl: string;
  /** Deployed Cake environment that selects the OAuth-compatible origin. */
  environment?: typeof ENV;
  /** True when Athena has already verified that the signed-in user is impersonating. */
  authenticatedImpersonation?: boolean;
  /** True when Athena has verified an internal employee session on the current origin. */
  authenticatedInternalUser?: boolean;
};

export const getImpersonationRedirectUrl = ({
  currentUrl,
  environment = ENV,
  authenticatedImpersonation = false,
  authenticatedInternalUser = false,
}: ImpersonationRedirectUrlOptions): string | null => {
  const current = new URL(currentUrl);
  const supportConfig = getSupportImpersonationConfig(environment);
  if (!supportConfig) {
    return null;
  }

  const isConfiguredOrigin =
    current.origin === supportConfig.publicOrigin ||
    current.origin === supportConfig.applicationOrigin;
  if (!isConfiguredOrigin) {
    return null;
  }

  const searchParams = current.searchParams;
  const isDashboardRoute =
    current.pathname === '/dashboard' || current.pathname.startsWith('/dashboard/');
  const hasCompleteSupportParameters = Boolean(
    isDashboardRoute && searchParams.get('customer') && searchParams.get('human_token'),
  );
  if (current.origin === supportConfig.applicationOrigin && authenticatedInternalUser) {
    return null;
  }
  if (!authenticatedImpersonation && !hasCompleteSupportParameters) {
    return null;
  }

  const destination = new URL(supportConfig.applicationOrigin);
  destination.pathname = current.pathname;
  destination.search = current.search;
  destination.hash = current.hash;
  const authEntry = new URL('/oauth2/start', supportConfig.authOrigin);
  authEntry.searchParams.set('rd', destination.toString());

  return authEntry.toString();
};

/**
 * Strips the login-lite access_token from the URL once the user is confirmed authenticated,
 * so it doesn't persist in browser history or get captured by analytics/RUM.
 * Must be a no-op for unauthenticated users so the login-lite redirect flow still works.
 */
export const useStripAccessTokenOnAuth = (authState: AuthInfo['state']) => {
  useEffect(() => {
    if (authState !== 'authenticated') {
      return;
    }
    const url = new URL(window.location.href);
    if (url.searchParams.has(LOGIN_LITE_ACCESS_TOKEN_QUERY_PARAM)) {
      url.searchParams.delete(LOGIN_LITE_ACCESS_TOKEN_QUERY_PARAM);
      window.history.replaceState(null, '', url.toString());
    }
  }, [authState]);
};

/**
 * Root-level context for auth information about the current user
 */
export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const { data: preparedData, error: preparedError } = useReadQuery(authQueryRef);
  const [fetch, { data: lazyData, error: lazyError }] = useLazyQuery(UserSessionDocument);

  const [prevLazyData, setPrevLazyData] = useState(lazyData);
  if (lazyData !== undefined && lazyData !== prevLazyData) {
    setPrevLazyData(lazyData);
  }

  const data = lazyData ?? prevLazyData ?? preparedData;
  const error = lazyError ?? preparedError;

  // The legacy /dashboard/customer/:uuid route reaches `?customer=` through <Navigate>, which
  // never reloads the page, so impersonation has to recompute on navigation.
  const { search } = useLocation();

  const authInfo = useMemo(() => {
    // loading from useLazyQuery doesn't apply — data is always resolved via the fallback chain
    const parsedAuth = parseAuthentication({ data, error, loading: false }, undefined);
    return identifyCustomer(parsedAuth, search);
  }, [data, error, search]);

  const impersonationRedirectUrl =
    typeof window !== 'undefined'
      ? getImpersonationRedirectUrl({
          currentUrl: window.location.href,
          authenticatedImpersonation: authInfo.isImpersonating,
          authenticatedInternalUser:
            authInfo.state === 'authenticated' && authInfo.user.hasOpendoorInternalAccess,
        })
      : null;
  const impersonatedCustomerUUID = useImpersonatedCustomer();
  const impersonationStateReady =
    authInfo.state !== 'authenticated' ||
    (authInfo.isImpersonating
      ? impersonatedCustomerUUID === authInfo.customerUUID
      : impersonatedCustomerUUID === null);

  useStripAccessTokenOnAuth(authInfo.state);

  // Auth errors and refetches both report `isImpersonating: false`, so releasing the guard on
  // anything short of a confirmed-authenticated session would unblock writes while the customer's
  // tree and Apollo cache are still mounted.
  //
  // Descendants stay unmounted until this state matches auth, so the write guard and Zeus client
  // are keyed to the effective customer before any child query or effect can run.
  useLayoutEffect(() => {
    if (authInfo.isImpersonating) {
      setImpersonatedCustomer(authInfo.customerUUID);
    } else if (authInfo.state === 'authenticated') {
      setImpersonatedCustomer(null);
    }

    if (impersonationRedirectUrl) {
      navigationHelpers.hardNavReplace(impersonationRedirectUrl, {
        label: 'cake-support-impersonation-auth-handoff',
      });
    }
  }, [authInfo.isImpersonating, authInfo.customerUUID, authInfo.state, impersonationRedirectUrl]);

  const refresh = useCallback(async () => {
    const result = await fetch();

    if (result.error) {
      throw toError(result.error);
    }
  }, [fetch]);

  const verifyLogin = useVerifyLogin(refresh);

  const context = useMemo<AuthContext>(
    () => ({
      ...authInfo,
      refresh,
      logout,
      verifyLogin,
    }),
    [authInfo, refresh, verifyLogin],
  );

  return impersonationRedirectUrl || !impersonationStateReady ? null : (
    <authContext.Provider value={context}>{children}</authContext.Provider>
  );
};

/**
 * Ensures the user is authenticated. If not, redirects to the login page.
 *
 * @throws if navigation fails
 */
const useAssertAuthenticated = () => {
  const context = useAuthContext();

  useLayoutEffect(() => {
    if (context.state === 'authenticated') {
      return;
    }

    navigationHelpers.hardNavAssign(getLoginUrl(window.location.href));
  }, [context.state]);

  return context.state;
};

/**
 * Renders children only when the user is authenticated. Shows a loading page and
 * redirects to login otherwise. Use this to wrap protected route layouts.
 */
export const AuthGate: FC<{ children: ReactNode }> = ({ children }) => {
  const state = useAssertAuthenticated();
  const isUnauthenticated = state !== 'authenticated';
  useTrackLoader('auth-gate', isUnauthenticated);

  if (isUnauthenticated) {
    return null;
  }

  return <>{children}</>;
};

/**
 * Helper to return the authenticated user for auth-gated routes
 *
 * @throws if the user is not authenticated
 */
export const useAuthAuthenticated = (triggeredQuery?: string): AuthenticatedCustomer & AuthFns => {
  const value = useAuthContext();

  if (value.state !== 'authenticated') {
    throw new UnauthenticatedError({ triggeredQuery, from: 'app-code' });
  }

  if (!value.customerUUID) {
    throw new SystemError('authenticated user missing customerUUID');
  }

  return {
    state: value.state,
    user: value.user,
    customerUUID: value.customerUUID,
    customerHumanToken: value.customerHumanToken,
    logout: value.logout,
    refresh: value.refresh,
    verifyLogin: value.verifyLogin,
  };
};

type RemoveSignOutResponse = {
  signed_out?: boolean;
  logged_in?: boolean;
};

/**
 * Helper to sign the user out of all devices
 *
 * @throws if sign out fails
 */
export const signOutOfAllDevices = async () => {
  try {
    // Same-origin path (not COSMOS_DOMAIN): cake serves from a different subdomain than
    // the dot-com backend, so an absolute cross-origin fetch is blocked by CORS. The
    // consumer edge worker proxies this path to the backend, keeping the request
    // same-origin so its response is readable.
    const resp = await fetch('/session/remote-sign-out', {
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    });
    if (!resp.ok) {
      throw new SystemError(`failed to sign out of all devices: ${resp.status}`);
    }

    const json = (await resp.json()) as RemoveSignOutResponse;

    if (json.signed_out === true || json.logged_in === false) {
      await clearGraphQLStores();
      navigationHelpers.hardPageReload();
    } else {
      throw new SystemError('sign out of all devices did not complete successfully');
    }
  } catch (e) {
    if (e instanceof SystemError) {
      throw e;
    }

    throw new SystemError(e as Error);
  }
};

const identifyCustomer = (auth: AuthenticationState, search: string): AuthInfo => {
  let customerUUIDOverride = undefined;
  let customerHumanTokenOverride = undefined;

  const isInternalUser = auth.user?.hasOpendoorInternalAccess;
  if (isInternalUser) {
    const searchParams = new URLSearchParams(search);

    // Only OD internal users can override the customer UUID
    // query param customer can be '' so using || to treat it as falsy
    customerUUIDOverride = (isInternalUser && searchParams.get('customer')) || undefined;

    // human_token is only meaningful during impersonation (when customer override is active)
    customerHumanTokenOverride = customerUUIDOverride
      ? searchParams.get('human_token') || undefined
      : undefined;
  }

  if (auth.state !== 'authenticated') {
    return {
      ...auth,
      user: auth.user ?? null,
      customerUUID: null,
      customerHumanToken: null,
      isImpersonating: false,
    };
  }

  // Authenticated user
  return {
    ...auth,
    customerUUID: customerUUIDOverride || auth.user.customerUuid,
    customerHumanToken: customerHumanTokenOverride || auth.user.humanToken,
    isImpersonating: !!customerUUIDOverride,
  };
};

/**
 * Logs the user out and reloads the page
 *
 * Uses domain from https://github.com/opendoor-labs/code/blob/master/js/packages/auth-fe/src/globals.ts#L1-L7
 */
const logout = async () => {
  try {
    const resp = await fetch(`${COSMOS_DOMAIN}/session/sign-out`, {
      method: 'GET',
      credentials: 'include',
    });

    if (!resp.ok) {
      throw new SystemError(`logout failed: ${resp.status}`);
    }

    // probably not necessary given the hard page reload, but a reminder for the future.
    await clearGraphQLStores();

    navigationHelpers.hardPageReload();
  } catch (e) {
    globalTrackError('logout', e as Error);
    navigationHelpers.hardNavAssign(LOGOUT_URL);
  }
};

type VerifyLoginPayload =
  | {
      google_id_token: string;
    }
  | {
      facebook_id_token: string;
    }
  | {
      human: {
        email: string;
        password: string;
      };
    };

const createVerifyLoginPayload = (credentials: LoginCredentials): VerifyLoginPayload => {
  if ('googleIdToken' in credentials) {
    return { google_id_token: credentials.googleIdToken };
  }

  if ('facebookIdToken' in credentials) {
    return { facebook_id_token: credentials.facebookIdToken };
  }

  return {
    human: {
      email: credentials.email,
      password: credentials.password,
    },
  };
};

type VerifyLoginError = { error?: string };

/**
 * Verifies the user's login credentials
 * @param credentials - The user's login credentials
 */
const useVerifyLogin = (refresh: () => Promise<void>) =>
  useCallback(
    async (credentials: LoginCredentials): Promise<LoginResult> => {
      const payload = createVerifyLoginPayload(credentials);
      let displayErrorMessage = DEFAULT_LOGIN_DISPLAY_ERROR_MESSAGE;

      /**
       * See `verify_login` at
       * https://github.com/opendoor-labs/web/blob/master/app/controllers/humans/sessions_controller.rb
       * Uses domain from https://github.com/opendoor-labs/code/blob/master/js/packages/auth-fe/src/globals.ts#L1-L7
       */
      let resp: Response | undefined;
      try {
        resp = await fetch(`${COSMOS_DOMAIN}/verify_login`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
      } catch (e) {
        globalTrackError('verify login request failed', e as Error);

        return {
          success: false,
          displayErrorMessage,
        };
      }

      if (resp?.ok) {
        try {
          await refresh();
          return { success: true };
        } catch (e) {
          globalTrackError('refresh after login failed', e as Error);
          return {
            success: false,
            displayErrorMessage,
          };
        }
      }

      try {
        const json = (await resp?.json()) as VerifyLoginError;
        displayErrorMessage = json.error || displayErrorMessage;
      } catch (e) {
        globalTrackError('login error without error message', e as Error);
      }

      return {
        success: false,
        displayErrorMessage,
      };
    },
    [refresh],
  );
